/**
 * P4-WP-7C.2: pure transformations for the Admin module-override editor.
 *
 * `membership.modules` is a SPARSE OVERRIDE MAP (WP-7C.1): a key present is an
 * explicit override, a key absent means the module inherits its role default.
 * The Admin editor previously held a DENSE 17-key map -- because the old GET
 * returned one -- and submitted all seventeen keys on every save, which turned
 * every inherited module into an explicit override the moment an admin changed
 * anything at all.
 *
 * These helpers keep the editor's working state sparse, so that what is saved
 * is exactly the set of explicit overrides and nothing else.
 *
 * Deliberately free of role, plan and ceiling policy. Every level shown to the
 * user -- effective, inherited, and whether an override is blocked by the plan
 * -- is supplied by the backend in the structured GET response. Recomputing
 * any of it here would put a second copy of authorization policy in the
 * browser, which is the divergence WP-7C.1 removed on the server.
 */

/** Build the sparse override map from the structured GET response. */
export function overridesFromView(view) {
  const overrides = {};
  Object.entries(view || {}).forEach(([moduleName, row]) => {
    if (row && row.override_level !== null && row.override_level !== undefined) {
      overrides[moduleName] = row.override_level;
    }
  });
  return overrides;
}

/** True when the module carries no explicit override and therefore inherits. */
export function isInherited(overrides, moduleName) {
  return !Object.prototype.hasOwnProperty.call(overrides || {}, moduleName);
}

/** Set an explicit override, returning a new map. */
export function setOverride(overrides, moduleName, level) {
  return { ...(overrides || {}), [moduleName]: level };
}

/**
 * Remove an explicit override, returning a new map.
 *
 * This is "reset this module to inheritance". It is NOT the same as the
 * editor's Reset button, which restores every module to whatever was loaded
 * from the server.
 */
export function clearOverride(overrides, moduleName) {
  const next = { ...(overrides || {}) };
  delete next[moduleName];
  return next;
}

/**
 * The level the segmented control should show as selected, or null when the
 * module is inheriting (in which case the Inherit option is selected).
 */
export function selectedLevel(overrides, moduleName) {
  return isInherited(overrides, moduleName) ? null : overrides[moduleName];
}

/**
 * What the admin should SEE as this module's access right now.
 *
 * While an override is pending-unsaved the honest answer is that override;
 * otherwise it is the backend's effective level. An inherited module shows
 * its inherited level -- never "off", which is what the old dense GET
 * displayed for every module a member held by inheritance.
 */
export function displayLevel(view, overrides, moduleName) {
  const row = (view || {})[moduleName] || {};
  if (!isInherited(overrides, moduleName)) return overrides[moduleName];
  return row.inherited_level !== undefined ? row.inherited_level : row.effective_level;
}

/**
 * True when the CURRENTLY SELECTED explicit override is one the plan blocks.
 *
 * Only meaningful for a saved override: `blocked_by_plan` is computed by the
 * backend from the stored value, so an unsaved edit is reported only when it
 * still matches what was stored.
 */
export function isBlockedByPlan(view, overrides, moduleName) {
  const row = (view || {})[moduleName] || {};
  if (isInherited(overrides, moduleName)) return false;
  return Boolean(row.blocked_by_plan) && row.override_level === overrides[moduleName];
}

/**
 * True when the CURRENTLY SELECTED explicit override is one GOVERNANCE
 * refuses for this membership's role.
 *
 * P4-WP-8A. Distinct from `isBlockedByPlan`: the plan flag means the Account
 * never bought the module, this one means the role may not hold it however
 * the Account is provisioned. Both come from the backend -- the browser holds
 * no role or plan policy of its own.
 */
export function isBlockedByGovernance(view, overrides, moduleName) {
  const row = (view || {})[moduleName] || {};
  if (isInherited(overrides, moduleName)) return false;
  return Boolean(row.blocked_by_governance) && row.override_level === overrides[moduleName];
}

/** Count modules whose override state differs between two sparse maps. */
export function countChanges(original, current) {
  const a = original || {};
  const b = current || {};
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  let changed = 0;
  keys.forEach((k) => {
    const inA = Object.prototype.hasOwnProperty.call(a, k);
    const inB = Object.prototype.hasOwnProperty.call(b, k);
    if (inA !== inB || a[k] !== b[k]) changed += 1;
  });
  return changed;
}

/**
 * The PUT payload: the complete set of explicit overrides, and only those.
 *
 * Inherited modules are omitted -- that omission is what makes them inherit,
 * because the endpoint replaces the whole stored map.
 */
export function serializeOverrides(overrides) {
  return { ...(overrides || {}) };
}
